/**
 * Country Code to Name Mapping Utility (Client-side)
 * Maps ISO 3166-1 alpha-2 country codes to full country names
 * Note: This is the client-side version. Server-side uses utils/countryNames.js
 */

const countryCodeToName = {
  // Common countries from security logs
  'CN': 'China',
  'HK': 'Hong Kong',
  'US': 'United States',
  'FR': 'France',
  'DE': 'Germany',
  'GB': 'United Kingdom',
  'IM': 'Isle of Man',
  'JP': 'Japan',
  'KR': 'South Korea',
  'IN': 'India',
  'RU': 'Russia',
  'BR': 'Brazil',
  'CA': 'Canada',
  'AU': 'Australia',
  'NL': 'Netherlands',
  'IT': 'Italy',
  'ES': 'Spain',
  'SE': 'Sweden',
  'NO': 'Norway',
  'DK': 'Denmark',
  'FI': 'Finland',
  'PL': 'Poland',
  'CH': 'Switzerland',
  'AT': 'Austria',
  'BE': 'Belgium',
  'IE': 'Ireland',
  'PT': 'Portugal',
  'GR': 'Greece',
  'CZ': 'Czech Republic',
  'HU': 'Hungary',
  'SK': 'Slovakia',
  'SI': 'Slovenia',
  'HR': 'Croatia',
  'BG': 'Bulgaria',
  'RO': 'Romania',
  'LT': 'Lithuania',
  'LV': 'Latvia',
  'EE': 'Estonia',
  'MT': 'Malta',
  'CY': 'Cyprus',
  'LU': 'Luxembourg',
  
  // High-risk countries for security
  'KP': 'North Korea',
  'BY': 'Belarus',
  'IR': 'Iran',
  'PK': 'Pakistan',
  'BD': 'Bangladesh',
  'VN': 'Vietnam',
  'ID': 'Indonesia',
  'TR': 'Turkey',
  'UA': 'Ukraine',
  'TH': 'Thailand',
  'MY': 'Malaysia',
  'PH': 'Philippines',
  'MX': 'Mexico',
  'AR': 'Argentina',
  'CO': 'Colombia',
  'PE': 'Peru',
  'CL': 'Chile',
  'VE': 'Venezuela',
  'EC': 'Ecuador',
  'UY': 'Uruguay',
  'PY': 'Paraguay',
  'BO': 'Bolivia',
  
  // Africa
  'BI': 'Burundi',
  'BJ': 'Benin',
  'RW': 'Rwanda',
  'ZA': 'South Africa',
  'NG': 'Nigeria',
  'KE': 'Kenya',
  'GH': 'Ghana',
  'EG': 'Egypt',
  'MA': 'Morocco',
  'TN': 'Tunisia',
  'DZ': 'Algeria',
  'LY': 'Libya',
  'SD': 'Sudan',
  'ET': 'Ethiopia',
  'UG': 'Uganda',
  'TZ': 'Tanzania',
  'ZW': 'Zimbabwe',
  'BW': 'Botswana',
  'ZM': 'Zambia',
  'MW': 'Malawi',
  'MZ': 'Mozambique',
  'AO': 'Angola',
  'CM': 'Cameroon',
  'CI': 'Ivory Coast',
  'SN': 'Senegal',
  'ML': 'Mali',
  'BF': 'Burkina Faso',
  'NE': 'Niger',
  'TD': 'Chad',
  'CF': 'Central African Republic',
  'CG': 'Republic of the Congo',
  'CD': 'Democratic Republic of the Congo',
  'GA': 'Gabon',
  'GQ': 'Equatorial Guinea',
  'ST': 'São Tomé and Príncipe',
  
  // Middle East
  'SA': 'Saudi Arabia',
  'AE': 'United Arab Emirates',
  'QA': 'Qatar',
  'KW': 'Kuwait',
  'BH': 'Bahrain',
  'OM': 'Oman',
  'JO': 'Jordan',
  'LB': 'Lebanon',
  'SY': 'Syria',
  'IQ': 'Iraq',
  'IL': 'Israel',
  'PS': 'Palestine',
  'YE': 'Yemen',
  'AF': 'Afghanistan',
  
  // Other Asia
  'SG': 'Singapore',
  'TW': 'Taiwan',
  'MO': 'Macau',
  'MN': 'Mongolia',
  'KZ': 'Kazakhstan',
  'UZ': 'Uzbekistan',
  'TM': 'Turkmenistan',
  'KG': 'Kyrgyzstan',
  'TJ': 'Tajikistan',
  'AM': 'Armenia',
  'AZ': 'Azerbaijan',
  'GE': 'Georgia',
  'MD': 'Moldova',
  'LK': 'Sri Lanka',
  'NP': 'Nepal',
  'BT': 'Bhutan',
  'MM': 'Myanmar',
  'KH': 'Cambodia',
  'LA': 'Laos',
  'BN': 'Brunei',
  'TL': 'East Timor',
  
  // Oceania
  'NZ': 'New Zealand',
  'FJ': 'Fiji',
  'PG': 'Papua New Guinea',
  'NC': 'New Caledonia',
  'VU': 'Vanuatu',
  'SB': 'Solomon Islands',
  'TO': 'Tonga',
  'WS': 'Samoa',
  'KI': 'Kiribati',
  'TV': 'Tuvalu',
  'NR': 'Nauru',
  'PW': 'Palau',
  'MH': 'Marshall Islands',
  'FM': 'Micronesia',
  
  // Caribbean
  'JM': 'Jamaica',
  'CU': 'Cuba',
  'DO': 'Dominican Republic',
  'HT': 'Haiti',
  'TT': 'Trinidad and Tobago',
  'BB': 'Barbados',
  'BS': 'Bahamas',
  'BZ': 'Belize',
  'GY': 'Guyana',
  'SR': 'Suriname',
  'GF': 'French Guiana',
  
  // Others
  'IS': 'Iceland',
  'GL': 'Greenland',
  'FO': 'Faroe Islands',
  'AD': 'Andorra',
  'MC': 'Monaco',
  'SM': 'San Marino',
  'VA': 'Vatican City',
  'LI': 'Liechtenstein',
  'AL': 'Albania',
  'MK': 'North Macedonia',
  'ME': 'Montenegro',
  'RS': 'Serbia',
  'BA': 'Bosnia and Herzegovina',
  'XK': 'Kosovo'
};

/**
 * Get full country name from country code
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code
 * @returns {string} Full country name or the original code if not found
 */
export function getCountryName(countryCode) {
  if (!countryCode) return 'Unknown';
  
  const upperCode = countryCode.toString().toUpperCase();
  return countryCodeToName[upperCode] || upperCode;
}

/**
 * Get country name with flag emoji
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code
 * @returns {string} Country name with flag emoji
 */
export function getCountryNameWithFlag(countryCode) {
  if (!countryCode || countryCode === 'UNKNOWN') return '🌍 Unknown';
  
  const upperCode = countryCode.toString().toUpperCase();
  const countryName = countryCodeToName[upperCode] || upperCode;
  
  // Convert country code to flag emoji
  const flag = upperCode.length === 2 
    ? upperCode.split('').map(char => String.fromCodePoint(char.charCodeAt(0) + 127397)).join('')
    : '🌍';
    
  return `${flag} ${countryName}`;
}

const countryNamesClient = {
  getCountryName,
  getCountryNameWithFlag,
  countryCodeToName
};

export default countryNamesClient;

